// The reference below is load-bearing, not tidiness: `build-defines.d.ts` is
// only in tsconfig.embed.json's `include`, but tsconfig.server.json compiles
// tests/** and therefore pulls this file into a SECOND program where the two
// ambient names would otherwise be unresolved. A triple-slash reference travels
// with the file into every program that includes it. The lint rule below prefers
// an ES import, which is impossible here: these are AMBIENT declarations for two
// esbuild `define` substitutions, so there is no module to import and nothing
// exists at runtime to import from.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./build-defines.d.ts" />

// src/embed/loader.ts  →  dist/e/l.js
//
// The file whose URL gets pasted into the client's WordPress page and then stays
// there. Everything about it is chosen for that fact: it is small, it is
// short-cached (netlify.toml, `/e/l.js`), it names the real payload rather than
// being it, and it finds its mount point in the way that survives the most
// hostile third-party page we can imagine.
//
// ── Why `querySelectorAll('[data-bsx]')` and NOT `document.currentScript` ─────
// The recon of https://www.bobandsheri.com/contests (docs/RECON-…md §5) proves a
// `<div>` with six `data-` attributes survives saves in that page's content
// field right now — it is what the incumbent uses. A `<script>` in page content
// is UNTESTED there, because there is no `<script>` in their content field to
// prove it either way, and WordPress strips script tags on save for any role
// without `unfiltered_html`.
//
// `document.currentScript` is also null under every JS-combining, Delay-JS and
// Rocket Loader plugin — the single most common reason a third-party embed
// renders nothing. None of those break a `querySelectorAll`. That is what makes
// Heather's still-unanswered question ("can a script tag live in the content
// field?") cost zero code change: whichever way it resolves, the `<div>` is the
// mount point and only the slot the one `<script>` line occupies changes.
//
// The `currentScript` branch below therefore exists ONLY so a script-only paste
// works for free. It is never depended upon and must never become the primary.
//
// ── No Subresource Integrity on the tag that loads this file, deliberately ────
// An `integrity` attribute pins the client's snippet to one file hash, so every
// bug fix would require them to edit WordPress again — the exact support ticket
// this product exists to eliminate, and the reason the one-year `immutable`
// cache on this path was removed in Plan 02-01. Same reasoning as the GACAB
// loader. The trust model is instead: our origin, our HTTPS, our rollback-able
// deploy, and — in iframe mode — `postMessage` validated on BOTH `e.origin` and
// `e.source` on every single message.
//
// ── Naming ───────────────────────────────────────────────────────────────────
// No `contest` / `promo` / `sweep` / `giveaway` / `widget` / `banner` in this
// file's URL, its filename, the attributes it reads or the element names it
// looks for. The client's /contests page runs Google AdSense
// (`ca-pub-6762097574148569`), so a real slice of that audience runs a filter
// list and those substrings are common matches. The one exception is the
// iframe's `title`, which is user-visible accessibility text read by screen
// readers, not a URL or a selector.

/**
 * Build-time constants. `__BSX_ORIGIN__` is empty only for the e2e build, where
 * falling back to `location.origin` makes the loader same-origin with whatever
 * host Playwright is serving. A production build bakes in the literal origin.
 * `__BSX_APP__` is the content-hashed payload path that THIS build emitted —
 * see scripts/build-embed.mjs, which fails loudly rather than guess it.
 *
 * Both are replaced by esbuild at build time. If a define is ever missing, the
 * bundle throws a ReferenceError on the first line rather than silently
 * substituting a default that points at the wrong payload.
 */
const ORIGIN = __BSX_ORIGIN__ || location.origin;
const APP = ORIGIN + __BSX_APP__;

/** The window flag guard 2 writes. Typed, so no `any` reaches the linter. */
type BsxWindow = Window & { __bsx?: number };

function iframeMount(node: Element, prop: string) {
  const f = document.createElement('iframe');
  f.src = ORIGIN + '/e/view/' + encodeURIComponent(prop) + location.search;
  f.title = 'Contests';
  // `setAttribute`, not `f.loading = 'lazy'`. The IDL property only reflects to
  // the attribute in engines that implement lazy loading; anywhere else the
  // assignment creates a plain expando and the attribute is never set, so the
  // hint is silently lost in exactly the old environments the iframe path exists
  // to serve. The attribute form is honoured by every engine that supports it
  // and ignored harmlessly by the rest. Measured 2026-08-06: happy-dom 20.11.1
  // does not reflect it, which is what surfaced this.
  f.setAttribute('loading', 'lazy');
  f.style.cssText = 'width:100%;border:0;display:block;min-height:420px';
  node.appendChild(f);
  addEventListener('message', (e: MessageEvent) => {
    // BOTH checks, on every message. `e.origin` alone still trusts any other
    // iframe served from our own origin on the same page; `e.source` alone
    // trusts nothing about where the frame came from.
    if (e.origin !== ORIGIN) return;
    if (e.source !== f.contentWindow) return;
    const d = e.data as { t?: string; h?: number };
    if (d && d.t === 'bsxh' && typeof d.h === 'number')
      f.style.height = Math.max(200, Math.min(20000, d.h)) + 'px';
  });
}

function inlineMount() {
  // Guard 2 — inject the payload once per document. This guards the PAYLOAD
  // INJECTION ONLY and must never be moved to wrap mounting: two `[data-bsx]`
  // divs on one page must both mount, and they share the one payload.
  const w = window as BsxWindow;
  if (w.__bsx) return;
  w.__bsx = 1;
  const s = document.createElement('script');
  s.async = true;
  s.src = APP;
  document.head.appendChild(s);
}

function scan() {
  let nodes = Array.prototype.slice.call(document.querySelectorAll('[data-bsx]')) as Element[];
  // Option A safety net: a script-only paste with no div anywhere. Never
  // depended upon — see the header. If it ever becomes load-bearing, the
  // combiner plugins above are one WordPress update away from breaking it.
  const cs = document.currentScript as HTMLScriptElement | null;
  if (!nodes.length && cs && cs.dataset.bsx) {
    const d = document.createElement('div');
    d.setAttribute('data-bsx', cs.dataset.bsx);
    // Carry a single-contest pin through the script-only paste too.
    if (cs.dataset.bsxContest) d.setAttribute('data-bsx-contest', cs.dataset.bsxContest);
    cs.parentNode!.insertBefore(d, cs);
    nodes = [d];
  }
  // The ONLY automatic route to the iframe path is an absent CAPABILITY, never
  // a caught runtime error. Silently falling back on an exception would hide a
  // real defect behind a degraded-but-working page, which contradicts the
  // standing "prefer loud failure over silent fallback" rule. Explicit
  // `data-bsx-mode="iframe"` is the launch-day recovery line — one attribute,
  // no rebuild — and Plan 06's beacon reports this branch as `iframe-fallback`
  // so we learn when it happened rather than inferring it.
  const capable = 'attachShadow' in Element.prototype && !!window.customElements;
  for (const n of nodes) {
    // Guard 3a — this node has already been handled.
    if (n.hasAttribute('data-bsx-mounted')) continue;
    // Guard 3b — this node already contains a mount, even without the marker.
    // Both checks are needed and they cover opposite failures: a page builder
    // that CLONES the section copies `data-bsx-mounted` onto an empty clone
    // (3a alone would leave it permanently blank), while a node that was moved
    // rather than cloned keeps its children (3b alone is what stops a second
    // grid appearing inside it).
    if (n.querySelector('bsx-mount, iframe')) continue;
    n.setAttribute('data-bsx-mounted', '1');
    const prop = n.getAttribute('data-bsx') || '';
    if (n.getAttribute('data-bsx-mode') === 'iframe' || !capable) iframeMount(n, prop);
    else inlineMount();
  }
}

scan();
if (document.readyState === 'loading') addEventListener('DOMContentLoaded', scan);
// Page builders, AJAX navigation and lazily-inserted sections all add the div
// late, and a theme that re-renders its content region REMOVES our element
// rather than duplicating it — the observer is what re-mounts in that case.
//
// It disconnects after 30 seconds. A permanent MutationObserver on a third
// party's document is not ours to leave running: it runs on every DOM change
// their page ever makes, for as long as the tab is open. 30 s covers every
// late-insertion case we know of; `pagehide` covers the rest.
const mo = new MutationObserver(scan);
mo.observe(document.documentElement, { childList: true, subtree: true });
setTimeout(() => mo.disconnect(), 30000);
addEventListener('pagehide', () => mo.disconnect());

// This file exports nothing and is executed for its side effects alone. The
// empty export is what makes it a MODULE rather than a global script, which the
// unit tests need in order to `await import()` it — and which also keeps `ORIGIN`,
// `APP` and `scan` out of the global scope of any program that compiles it.
export {};
